// src/components/buttons/Button.tsx
import { Button as AntdButton, ButtonProps as AntdButtonProps } from "antd";
import { addKeyIfNotUndefined } from "common/utils/universal/object";
import React, { forwardRef } from "react";
import * as styles from "./Button.css";

type IconPosition = "start" | "end";
export type Variant =
  | "danger"
  | "dangerSecondary"
  | "gradient"
  | "primary"
  | "secondary"
  | "success"
  | "text"
  | "utility";
export interface ButtonProps extends AntdButtonProps {
  ariaLabel?: string;
  dataTestId?: string;
  iconPosition?: IconPosition; // Icon position: start or end
  variant?: Variant;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      ariaLabel,
      children,
      className,
      dataTestId,
      icon,
      iconPosition = "start",
      type,
      variant = "utility",
      ...rest
    },
    ref
  ) => {
    // Only apply custom styles for variants that are not handled by AntD's type prop
    const customVariant =
      !type || !["primary", "link", "text"].includes(type)
        ? variant
        : undefined;

    const variantClass =
      customVariant &&
      styles.buttonVariants[customVariant as keyof typeof styles.buttonVariants]
        ? styles.buttonVariants[
            customVariant as keyof typeof styles.buttonVariants
          ]
        : undefined;

    // Compose class names: base + variant
    const composedClassName = [styles.baseButton, variantClass, className]
      .filter(Boolean)
      .join(" ");

    return (
      <AntdButton
        {...rest}
        {...addKeyIfNotUndefined("data-testid", dataTestId)}
        aria-label={ariaLabel} // Accessible label
        className={composedClassName}
        icon={iconPosition === "start" ? icon : undefined}
        ref={ref}
        type={
          ["primary", "text"].includes(variant)
            ? (variant as AntdButtonProps["type"])
            : type
        }
      >
        {children}
        {iconPosition === "end" && icon}
      </AntdButton>
    );
  }
) as React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
  Group: typeof AntdButton.Group;
};

Button.Group = AntdButton.Group;

export default Button;
